import { SITE_NAME, SITE_URL } from '../../constants/site';
import { blogPath } from '../../utils/slug';
import { htmlToText } from '../../utils/sanitizeHtml';

/*
 * Shows the author what this post will look like in a Google result and as a
 * social card, plus the SEO problems worth fixing before publishing.
 *
 * The point is that these limits are invisible while writing: a 90-character
 * title reads fine in the form and gets cut off in search results, and a missing
 * excerpt means Google invents a snippet from the body.
 */

// Practical display limits. Google measures pixels, not characters, so these are
// approximations — but they are the approximations that stop text being cut off.
const TITLE_MAX = 60;
const DESC_MAX = 155;
const TITLE_MIN = 20;

const clip = (text, max) =>
  text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;

function SeoPreview({ post }) {
  const title = (post.title || '').trim();
  const excerpt = (post.excerpt || '').trim();
  const bodyText = htmlToText(post.content || '');

  // Mirrors what Seo.js will actually emit: the excerpt if there is one,
  // otherwise the opening of the body.
  const description = excerpt || bodyText;

  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME}`;
  const path = title && post.category ? blogPath({ ...post, blogId: post.blogId || 'new' }) : '';

  const issues = [];
  if (!title) issues.push('Title is required.');
  else if (title.length < TITLE_MIN)
    issues.push(`Title is short (${title.length} chars) — aim for ${TITLE_MIN}–${TITLE_MAX}.`);
  else if (fullTitle.length > TITLE_MAX)
    issues.push(
      `Title + site name is ${fullTitle.length} chars; Google shows about ${TITLE_MAX}. It will be cut off.`
    );

  if (!excerpt)
    issues.push('No excerpt — Google will invent a snippet, and social cards will use raw body text.');
  else if (excerpt.length > DESC_MAX)
    issues.push(`Excerpt is ${excerpt.length} chars; about ${DESC_MAX} is shown.`);

  if (!post.image) issues.push('No cover image — cards and social previews fall back to a placeholder.');
  if (!post.category) issues.push('No category — needed for the URL and the category page.');
  if (bodyText.split(' ').filter(Boolean).length < 300)
    issues.push('Body is under 300 words, which tends to read as thin content.');

  return (
    <div className="seo-preview">
      <h3 className="seo-preview-title">Search result preview</h3>

      <div className="serp">
        <p className="serp-url">
          {SITE_URL.replace(/^https?:\/\//, '')}
          {path && <span className="serp-crumb"> › {path.split('/').filter(Boolean).join(' › ')}</span>}
        </p>
        <p className="serp-headline">{clip(fullTitle, TITLE_MAX)}</p>
        <p className="serp-desc">
          {description ? clip(description, DESC_MAX) : 'No description available yet.'}
        </p>
      </div>

      {path && (
        <p className="seo-preview-url">
          <span className="field-label">Public URL</span>
          <code>{path}</code>
        </p>
      )}

      <h3 className="seo-preview-title">Checks</h3>
      {issues.length === 0 ? (
        <p className="seo-ok">Everything looks good.</p>
      ) : (
        <ul className="seo-issues">
          {issues.map((issue) => (
            <li key={issue}>{issue}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default SeoPreview;
